import React from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  siblingCount = 1,
  itemsPerPageOptions = [5, 10, 20, 50, 100],
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getPageNumbers = () => {
    // Always show first and last page
    const rangeStart = Math.max(2, currentPage - siblingCount);
    const rangeEnd = Math.min(totalPages - 1, currentPage + siblingCount);

    const pages = [];
    let previousPage = null;

    // Always add the first page
    pages.push(1);

    // Add dots before the range if needed
    if (rangeStart > 2) {
      pages.push('...');
    }

    // Add the page numbers in the range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    // Add dots after the range if needed
    if (rangeEnd < totalPages - 1) {
      pages.push('...');
    }

    // Add the last page if there's more than one page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    // Filter out duplicate dots
    const filteredPages = pages.filter((page, index) => {
      if (page !== '...') return true;
      return index === 0 || index === pages.length - 1 || pages[index - 1] !== '...';
    });

    return filteredPages;
  };

  const handlePerPageChange = (e) => {
    onItemsPerPageChange(parseInt(e.target.value));
    onPageChange(1); // Reset to first page when changing items per page
  };

  return (
    <div className="flex  items-center justify-between mt-0 gap-4 mb-4 flex-wrap">
      {/* Items per page */}
      <div className="flex items-center gap-2">
        <label htmlFor="itemsPerPage" className="xs:text-sm text-xs text-gray-600">
          Items per page:
        </label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={handlePerPageChange}
          className="border px-2 py-1 rounded xs:text-sm text-xs"
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-1 flex-wrap justify-center">
        <button
          className="px-3 py-1.5 border rounded disabled:opacity-40"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <IoChevronBack />
        </button>

        {/* Mobile view (only current page) */}
        <div className="xs:hidden flex items-center">
          <button
            className={`px-3 py-1 border rounded text-xs min-w-[36px] ${
              'bg-black text-white'
            }`}
          >
            {currentPage}
          </button>
        </div>

        {/* Desktop view (full pagination) */}
        <div className="hidden xs:flex items-center gap-1">
          {getPageNumbers().map((page, index) =>
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 border rounded xs:text-sm text-xs min-w-[36px] ${
                  currentPage === page ? 'bg-black text-white' : 'hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            )
          )}
        </div>

        <button
          className="px-3 py-1.5 border rounded disabled:opacity-40"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          <IoChevronForward />
        </button>
      </div>
    </div>
  );
};

export default Pagination;